import {
  ActionIcon,
  Box,
  Button,
  Divider,
  Group,
  NumberInput,
  Stack,
  TextInput,
  Textarea,
  Title,
  useMatches,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { useLocalStorage } from "@mantine/hooks";
import dayjs from "dayjs";
import { isEmpty } from "lodash";
import { zod4Resolver } from "mantine-form-zod-resolver";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { CurrencyInput, Dropdown, FormField } from "@/components/elements";
import { ConditionsTooltipContent } from "@/components/elements/TooltipContent";
import { IconMinus, IconPlus } from "@/components/icons";
import { defaultCurrency } from "@/lib/currency";
import { UpdateCarInput, editCarSchema } from "@/services/cars/update-car";
import { usePlansUser } from "@/services/plans/get-user-plan";
import { useProfile } from "@/services/profile/get-profile";
import { Currency } from "@/types/currency";
import { formatDate } from "@/utils/formatDate";

import type { FC } from "react";

export type EditCarFormProps = {
  onSubmit: (values: UpdateCarInput) => void;
  onCancel?: () => void;
  initialValues?: Partial<UpdateCarInput>;
  loading?: boolean;
  labels?: {
    cancelButton?: string;
    submitButton?: string;
  };
};

export const EditCarForm: FC<EditCarFormProps> = ({
  onSubmit,
  onCancel,
  initialValues,
  loading = false,
  labels,
}) => {
  const { t } = useTranslation();
  const [showAdditional, setShowAdditional] = useState<boolean>(false);

  const { data: user } = useProfile();
  const { data: plans } = usePlansUser();

  const firstName = user?.additional_info?.first_name ?? user?.name ?? "";
  const lastName = user?.additional_info?.last_name ?? "";
  const nameParts = [firstName, lastName].filter((p) => p !== undefined && p !== null && p !== "");
  const fullName = nameParts.join(" ");

  const [currency] = useLocalStorage<Currency>({
    key: "currency",
    defaultValue: defaultCurrency,
  });

  const isFreePlan = plans?.name === "starter";

  const form = useForm<UpdateCarInput>({
    initialValues: {
      nickname: "",
      owner: fullName || "",
      purchase_price: 0,
      purchase_date: dayjs(new Date()).format("YYYY-MM-DD"),
      licence_plate: "",
      country_of_registration: "",
      color: "",
      interior_colors: "",
      condition: "",
      number_of_previous_owner: 0,
      special_equipment: "",
      vin: "",
      ...initialValues,
    },
    validate: zod4Resolver(editCarSchema),
    validateInputOnBlur: true,
  });

  const handleSubmit = (values: UpdateCarInput) => {
    onSubmit({
      ...values,
      purchase_date: dayjs(values.purchase_date).format("YYYY-MM-DD"),
      condition: values.condition ? values.condition : "",
    });
  };

  const isMobile = useMatches({ base: true, md: false });
  const WrapperForFields = isMobile ? Stack : Group;
  const wrapperProps = isMobile ? { gap: "md" } : { grow: true, align: "flex-start" };

  const isEdit = !isEmpty(initialValues);

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="md">
        <Title order={4}>{t("components.CreateCarForm.titles.main")}</Title>

        <WrapperForFields {...wrapperProps}>
          {!isEdit ? (
            <>
              <FormField required>
                <Dropdown
                  label={t("car.status")}
                  placeholder={t("components.CreateCarForm.placeholders.status")}
                  data={[
                    { value: "owned", label: t("car.statuses.owned") },
                    { value: "sold", label: t("car.statuses.sold") },
                    { value: "traded", label: t("car.statuses.traded") },
                    { value: "totaled", label: t("car.statuses.totaled") },
                  ]}
                  {...form.getInputProps("status")}
                />
              </FormField>
            </>
          ) : null}
          <FormField required>
            <TextInput
              label={t("car.owner")}
              placeholder={t("components.CreateCarForm.placeholders.owner")}
              {...form.getInputProps("owner")}
            />
          </FormField>
          {isEdit ? <Box>&nbsp;</Box> : null}
        </WrapperForFields>

        <WrapperForFields {...wrapperProps}>
          <FormField required>
            <CurrencyInput
              label={t("car.purchase_price")}
              value={(Number(form.values.purchase_price) || 0) * (Number(currency.value) || 1)}
              placeholder={t("components.CreateCarForm.placeholders.purchase_price", "Price")}
              currencySymbol={currency.symbol}
              onChange={(value) => {
                const adjustedValue = (Number(value) || 0) / (Number(currency.value) || 1);
                form.setFieldValue("purchase_price", adjustedValue);
              }}
            />
          </FormField>
          <FormField required>
            <DatePickerInput
              label={t("car.purchase_date")}
              placeholder={t("components.CreateCarForm.placeholders.purchase_date", "Select date")}
              valueFormat="YYYY-MM-DD"
              valueFormatter={(value) => formatDate(value.date as string)}
              {...form.getInputProps("purchase_date")}
            />
          </FormField>
        </WrapperForFields>

        <Divider my="lg" />

        <Group>
          <Title order={4}>{t("components.CreateCarForm.titles.details")}</Title>
          <ActionIcon size="md" onClick={() => setShowAdditional(!showAdditional)}>
            {showAdditional ? <IconMinus /> : <IconPlus />}
          </ActionIcon>
        </Group>

        {showAdditional && (
          <>
            <WrapperForFields {...wrapperProps}>
              <TextInput
                label={t("car.licence_plate")}
                placeholder={t("components.CreateCarForm.placeholders.licence_plate")}
                {...form.getInputProps("licence_plate")}
              />
              <TextInput
                label={t("car.country_of_registration")}
                placeholder={t("components.CreateCarForm.placeholders.country_of_registration")}
                {...form.getInputProps("country_of_registration")}
              />
            </WrapperForFields>

            <WrapperForFields {...wrapperProps}>
              <FormField>
                <TextInput
                  label={t("car.vin")}
                  placeholder={t("components.CreateCarForm.placeholders.vin")}
                  {...form.getInputProps("vin")}
                />
              </FormField>

              <FormField>
                <TextInput
                  label={t("car.color")}
                  placeholder={t("components.CreateCarForm.placeholders.color")}
                  {...form.getInputProps("color")}
                />
              </FormField>
            </WrapperForFields>
            <WrapperForFields {...wrapperProps}>
              <FormField>
                <TextInput
                  label={t("car.interior_colors")}
                  placeholder={t("components.CreateCarForm.placeholders.interior_colors")}
                  {...form.getInputProps("interior_colors")}
                />
              </FormField>
              <Dropdown
                label={t("car.condition")}
                tooltip={<ConditionsTooltipContent />}
                disabled={isFreePlan}
                clearable
                placeholder={t("components.CreateCarForm.placeholders.condition")}
                data={[
                  { value: "superb", label: t("car.conditions.superb") },
                  { value: "excellent", label: t("car.conditions.excellent") },
                  { value: "good", label: t("car.conditions.good") },
                  { value: "fair", label: t("car.conditions.fair") },
                ]}
                value={form.values.condition}
                onChange={(value) => {
                  // Check that it is one of the allowed options
                  const allowedValues = ["superb", "excellent", "good", "fair"] as const;
                  const safeValue = allowedValues.includes(value as any)
                    ? (value as (typeof allowedValues)[number])
                    : undefined;

                  form.setFieldValue("condition", safeValue);
                }}
                error={form.errors.condition}
              />
            </WrapperForFields>

            <WrapperForFields {...wrapperProps}>
              <NumberInput
                label={t("car.number_of_previous_owner")}
                placeholder={t("components.CreateCarForm.placeholders.number_of_previous_owner")}
                min={0}
                {...form.getInputProps("number_of_previous_owner")}
              />
              <TextInput
                label={t("car.nickname")}
                placeholder={t("components.CreateCarForm.placeholders.nickname")}
                {...form.getInputProps("nickname")}
              />
            </WrapperForFields>

            <Textarea
              label={t("car.special_equipment")}
              placeholder={t("components.CreateCarForm.placeholders.special_equipment")}
              minRows={4}
              {...form.getInputProps("special_equipment")}
            />
          </>
        )}

        <Group justify="center" mt="md">
          {onCancel && (
            <Button
              variant="outline"
              onClick={onCancel}
              disabled={loading}
              aria-label={t("components.Form.cancelButton")}
            >
              {labels?.cancelButton || t("components.Form.cancelButton")}
            </Button>
          )}
          <Button
            type="submit"
            disabled={!form.isValid() || loading || !form.isDirty()}
            loading={loading}
            aria-label={t("components.CreateCarForm.submitButton")}
          >
            {labels?.submitButton || t("components.CreateCarForm.submitButton")}
          </Button>
        </Group>
      </Stack>
    </form>
  );
};
